import {
  DEFAULT_CHECKLIST_SHELL,
  mergeAllChecklists,
  resolveAnnualReportChecklistKey,
  buildChecklistDefaultValues,
  getChecklistProgressFields,
  DEFAULT_ANNUAL_REPORT_CHECKLISTS,
} from './annualReportChecklistConfig';

export {
  resolveAnnualReportChecklistKey,
  buildChecklistDefaultValues,
  getChecklistProgressFields,
  DEFAULT_ANNUAL_REPORT_CHECKLISTS,
  DEFAULT_CHECKLIST_SHELL,
} from './annualReportChecklistConfig';

const DEFAULT_STEP_CARDS = [
  { step: 1, title: 'Initial Information Required', instructions: '' },
  { step: 2, title: 'Annual Report Filed', instructions: 'Administrative review and processing of your annual report.' },
];

const DEFAULT_STEP_INSTRUCTIONS = {
  step1Default: 'Fill the required business details for {state} Annual Report.',
  step1Wyoming: 'Fill the mailing, office, and asset details for Wyoming Annual Report.',
  step1NewJersey: 'Fill the agent, office, and director details for New Jersey Annual Report.',
  step1DelawareLLC: 'Fill the agent and tax year details for Delaware LLC.',
  step1DelawareCCorp: 'Fill the officer, director, and business details for Delaware C-CORP.',
  step2: 'Administrative review and processing of your annual report.',
};

const formatTemplate = (template, vars = {}) =>
  (template || '').replace(/\{(\w+)\}/g, (_, key) => (vars[key] != null ? String(vars[key]) : ''));

export const getAnnualReportPageMeta = (content) => {
  const stepCards = (content?.stepCards?.length ? content.stepCards : DEFAULT_STEP_CARDS).map((card, index) => ({
    ...DEFAULT_STEP_CARDS[index],
    ...card,
  }));

  return {
    pageTitle: content?.pageTitle || 'Annual Report Service',
    pageSubtitle: content?.pageSubtitle || 'Complete your state annual report checklist and track filing deadlines',
    pageIcon: content?.pageIcon || 'LuCalendarDays',
    loadingMessage: content?.loadingMessage || 'Loading your annual report data...',
    dueDateCardTitle: content?.dueDateCardTitle || 'Annual Report Due Date',
    labelState: content?.labelState || 'State',
    labelEntityType: content?.labelEntityType || 'Entity Type',
    labelFormationDate: content?.labelFormationDate || 'Formation Date',
    labelFilingYear: content?.labelFilingYear || 'Filing Year',
    labelDueDate: content?.labelDueDate || 'Due Date',
    stepCards,
    stepInstructions: { ...DEFAULT_STEP_INSTRUCTIONS, ...(content?.stepInstructions || {}) },
    statusBadges: {
      inProgress: content?.statusBadges?.inProgress || 'IN PROGRESS',
      clientPending: content?.statusBadges?.clientPending || 'ACTION REQUIRED',
      completed: content?.statusBadges?.completed || 'COMPLETED',
      pending: content?.statusBadges?.pending || 'PENDING',
    },
    stepLabels: {
      progress: content?.stepLabels?.progress || 'Progress',
      actionRequired: content?.stepLabels?.actionRequired || 'Action Required',
      completed: content?.stepLabels?.completed || 'Completed',
      workingForEmployee: content?.stepLabels?.workingForEmployee || 'Working for Employee',
    },
    recordsTableDefaultTitle: content?.recordsTableDefaultTitle || 'Annual Report Records',
    emptyStateTitle: content?.emptyStateTitle || 'Annual Report Setup Pending',
    emptyStateDescription:
      content?.emptyStateDescription ||
      'Our team is currently setting up your annual report tracking. Once the administrative setup is complete, your due dates and filing records will appear here.',
    emptyStateButtonLabel: content?.emptyStateButtonLabel || 'Check for Updates',
    errorLabel: content?.errorLabel || 'Error:',
    errorRetryLabel: content?.errorRetryLabel || 'Try Again',
    errorRefreshingLabel: content?.errorRefreshingLabel || 'Refreshing...',
    toasts: { ...(content?.toasts || {}) },
    checklistShell: { ...DEFAULT_CHECKLIST_SHELL, ...(content?.checklistShell || {}) },
    checklists: mergeAllChecklists(content?.checklists),
  };
};

export const getAnnualReportChecklist = (meta, service) => {
  const key = resolveAnnualReportChecklistKey(service);
  return meta.checklists?.[key] || DEFAULT_ANNUAL_REPORT_CHECKLISTS[key];
};

export const getAnnualReportStepTitle = (meta, step) =>
  meta.stepCards.find((card) => card.step === step)?.title ||
  DEFAULT_STEP_CARDS.find((card) => card.step === step)?.title ||
  `Step ${step}`;

export const getAnnualReportStep1Instruction = (meta, annualReportService) => {
  const { stepInstructions } = meta;
  if (annualReportService?.state === 'Wyoming') return stepInstructions.step1Wyoming;
  if (annualReportService?.state === 'New Jersey') return stepInstructions.step1NewJersey;
  if (annualReportService?.state === 'Delaware') {
    return annualReportService?.entityType === 'LLC'
      ? stepInstructions.step1DelawareLLC
      : stepInstructions.step1DelawareCCorp;
  }
  return formatTemplate(stepInstructions.step1Default, { state: annualReportService?.state || 'Florida' });
};

export const getAnnualReportStepInstruction = (meta, step, annualReportService) => {
  if (step === 1) return getAnnualReportStep1Instruction(meta, annualReportService);
  const card = meta.stepCards.find((c) => c.step === step);
  return card?.instructions || meta.stepInstructions.step2;
};

export const buildDefaultAnnualReportSteps = (meta) =>
  meta.stepCards.map((card) => ({
    step: card.step,
    title: card.title,
    status: card.step === 1 ? 'in-progress' : 'pending',
    ...(card.step === 1 ? { answers: {} } : {}),
  }));
